import { rememberProject } from '../../utils/projectPreference';
/**
 * Composant ProjectSwitcher
 *
 * Sélecteur de projet épuré pour la sidebar
 * Permet de changer de projet rapidement sans quitter la page
 */

import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown, Briefcase, Check } from 'lucide-react';
import backendApi from '../../services/backendApi';

const ProjectSwitcher = ({ isCollapsed = false }) => {
  const [projects, setProjects] = useState([]);
  const [currentProject, setCurrentProject] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const dropdownRef = useRef(null);

  // Récupérer le projectId actuel depuis localStorage
  const currentProjectId = typeof localStorage !== 'undefined'
    ? parseInt(localStorage.getItem('selected_project_id'))
    : null;

  /**
   * Charger les projets disponibles
   */
  useEffect(() => {
    const fetchProjects = async () => {
      try {
        setLoading(true);
        const response = await backendApi.getProjects();
        setProjects(response.projects || []);

        // Trouver le projet actuel
        if (currentProjectId && response.projects) {
          const current = response.projects.find(p => p.id === currentProjectId);
          setCurrentProject(current || null);
        }
      } catch (error) {
        console.error('Erreur chargement projets:', error);
        setProjects([]);
      } finally {
        setLoading(false);
      }
    };

    fetchProjects();
  }, [currentProjectId]);

  /**
   * Fermer le dropdown quand on clique en dehors
   */
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isOpen]);

  /**
   * Changer de projet
   */
  const handleProjectChange = (projectId) => {
    if (projectId === currentProjectId) {
      setIsOpen(false);
      return;
    }

    const project = projects.find(p => p.id === projectId);
    if (project) rememberProject(project);

    window.location.reload();
  };

  if (loading || !currentProject) {
    return null;
  }

  return (
    <div data-tour="project" className="horizon-project-switcher relative px-3 py-4 border-b border-indigo-500/30" ref={dropdownRef}>
      {/* Bouton principal */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`
          w-full flex items-center gap-3 px-3 py-2.5 rounded-lg
          bg-white/10 hover:bg-white/15 transition-all duration-200
          ${isCollapsed ? 'justify-center' : ''}
        `}
        title={isCollapsed ? currentProject.name : ''}
      >
        <Briefcase className="w-5 h-5 text-white flex-shrink-0" />

        {!isCollapsed && (
          <>
            <div className="flex-1 text-left min-w-0">
              <p className="text-xs text-indigo-200 mb-0.5">Projet actif</p>
              <p className="text-sm font-medium text-white truncate">
                {currentProject.name}
              </p>
            </div>
            <ChevronDown
              className={`w-4 h-4 text-indigo-200 transition-transform flex-shrink-0 ${
                isOpen ? 'rotate-180' : ''
              }`}
            />
          </>
        )}
      </button>

      {/* Dropdown des projets */}
      {isOpen && !isCollapsed && projects.length > 1 && (
        <div className="absolute left-3 right-3 top-full mt-2 bg-white rounded-lg shadow-xl border border-gray-200 overflow-hidden z-50 max-h-80 overflow-y-auto">
          {projects.map((project) => {
            const isSelected = project.id === currentProjectId;
            return (
              <button
                key={project.id}
                onClick={() => handleProjectChange(project.id)}
                className={`
                  w-full px-4 py-3 text-left hover:bg-indigo-50 transition-colors
                  flex items-center gap-3 border-b border-gray-100 last:border-b-0
                  ${isSelected ? 'bg-indigo-50' : ''}
                `}
              >
                <Briefcase className={`w-4 h-4 flex-shrink-0 ${
                  isSelected ? 'text-indigo-600' : 'text-gray-400'
                }`} />
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-medium truncate ${
                    isSelected ? 'text-indigo-900' : 'text-gray-900'
                  }`}>
                    {project.name}
                  </p>
                  {project.partnerName && (
                    <p className="text-xs text-gray-500 truncate">
                      {project.partnerName}
                    </p>
                  )}
                </div>
                {isSelected && (
                  <Check className="w-4 h-4 text-indigo-600 flex-shrink-0" />
                )}
              </button>
            );
          })}
        </div>
      )}

      {/* Dropdown pour mode collapsed */}
      {isOpen && isCollapsed && projects.length > 1 && (
        <div className="absolute left-full top-0 ml-2 bg-white rounded-lg shadow-xl border border-gray-200 overflow-hidden z-50 w-64 max-h-80 overflow-y-auto">
          <div className="px-3 py-2 bg-indigo-50 border-b border-indigo-100">
            <p className="text-xs font-semibold text-indigo-900 uppercase tracking-wide">
              Changer de projet
            </p>
          </div>
          {projects.map((project) => {
            const isSelected = project.id === currentProjectId;
            return (
              <button
                key={project.id}
                onClick={() => handleProjectChange(project.id)}
                className={`
                  w-full px-4 py-3 text-left hover:bg-indigo-50 transition-colors
                  flex items-center gap-3 border-b border-gray-100 last:border-b-0
                  ${isSelected ? 'bg-indigo-50' : ''}
                `}
              >
                <Briefcase className={`w-4 h-4 flex-shrink-0 ${
                  isSelected ? 'text-indigo-600' : 'text-gray-400'
                }`} />
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-medium truncate ${
                    isSelected ? 'text-indigo-900' : 'text-gray-900'
                  }`}>
                    {project.name}
                  </p>
                  {project.partnerName && (
                    <p className="text-xs text-gray-500 truncate">
                      {project.partnerName}
                    </p>
                  )}
                </div>
                {isSelected && (
                  <Check className="w-4 h-4 text-indigo-600 flex-shrink-0" />
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ProjectSwitcher;
